<script setup lang="ts">
import EditionCard from "@/components/EditionCard.vue";
import { EngineType } from "embla-carousel";
import emblaCarouselVue from "embla-carousel-vue";
import { onMounted, ref } from "vue";
import LoadingIndicator from "@/components/general/LoadingIndicator.vue";
import { Edition } from "@/types/edition";

const props = defineProps<{
  hasNextPage: boolean;
  editions: Edition[];
}>();

const emit = defineEmits(["init", "fetchNextPage"]);

const listenForScroll = ref(true);
const loadingMore = ref(false);

const [emblaRef, emblaApi] = emblaCarouselVue({
  dragFree: true,
  containScroll: "keepSnaps",
  watchResize: false,
  watchSlides: (emblaApi) => {
    const reloadEmbla = (): void => {
      const oldEngine = emblaApi.internalEngine();

      emblaApi.reInit();
      const newEngine = emblaApi.internalEngine();
      const copyEngineModules: (keyof EngineType)[] = [
        "scrollBody",
        "location",
        "offsetLocation",
        "previousLocation",
        "target",
      ];
      copyEngineModules.forEach((engineModule) => {
        Object.assign(newEngine[engineModule], oldEngine[engineModule]);
      });

      newEngine.translate.to(oldEngine.location.get());
      const { index } = newEngine.scrollTarget.byDistance(0, false);
      newEngine.index.set(index);
      newEngine.animation.start();
      emblaApi.reInit();

      loadingMore.value = false;
      listenForScroll.value = true;
    };

    const reloadAfterPointerUp = (): void => {
      emblaApi.off("pointerUp", reloadAfterPointerUp);
      reloadEmbla();
    };

    const engine = emblaApi.internalEngine();

    if (props.hasNextPage && engine.dragHandler.pointerDown()) {
      const boundsActive = engine.limit.reachedMax(engine.target.get());
      engine.scrollBounds.toggleActive(boundsActive);
      emblaApi.on("pointerUp", reloadAfterPointerUp);
    } else {
      reloadEmbla();
    }
  },
});

const handleScroll = () => {
  if (!emblaApi.value || !listenForScroll.value) return;
  const lastSlide = emblaApi.value.slideNodes().length - 1;
  const lastSlideInView = emblaApi.value.slidesInView().includes(lastSlide);
  const shouldLoadMore = !loadingMore.value && lastSlideInView;
  if (shouldLoadMore) {
    listenForScroll.value = false;
    loadMore();
  }

  loadingMore.value = loadingMore.value || lastSlideInView;
};

const loadMore = () => {
  if (props.hasNextPage) {
    emit("fetchNextPage");
  }
};

onMounted(() => {
  emit("init", emblaApi.value);

  if (!emblaApi.value) return;
  emblaApi.value.on("scroll", handleScroll);

  const onResize = () => emblaApi.value?.reInit();
  window.addEventListener("resize", onResize);
  emblaApi.value.on("destroy", () =>
    window.removeEventListener("resize", onResize)
  );
});
</script>

<template>
  <div class="px-4 md:px-8 overflow-hidden" ref="emblaRef">
    <div class="flex">
      <div
        v-for="edition in editions"
        :key="edition.id"
        class="shrink-0 min-w-0 mr-5"
      >
        <EditionCard :edition="edition" class="w-32 md:w-44" />
      </div>
      <div class="w-16 shrink-0 flex-center text-icon-brand" v-if="hasNextPage">
        <LoadingIndicator />
      </div>
    </div>
  </div>
</template>
